import React from 'react';
import { Users, X } from 'lucide-react';

const OVERFLOW_OPTIONS = [
  { value: 'even_split', label: 'Distribute evenly as overflow' },
  { value: 'manager_fallback', label: 'Assign to manager' },
];

function Avatar({ agent }) {
  if (agent?.avatar) {
    return <img src={agent.avatar} alt="" className="w-7 h-7 rounded-full object-cover" />;
  }
  const initials = String(agent?.name || agent?.email || '?').trim().slice(0, 1).toUpperCase();
  return (
    <div className="w-7 h-7 rounded-full bg-civix-purple/30 flex items-center justify-center text-xs font-bold">
      {initials}
    </div>
  );
}

/**
 * Progress badge for "received today / daily cap".
 * @param {{ count: number, cap: number }} props
 */
function ReceivedToday({ count, cap }) {
  const reached = count >= cap;
  const pct = Math.min(100, Math.round((count / Math.max(cap, 1)) * 100));
  return (
    <div className="min-w-[120px]">
      <div className="flex items-center justify-between text-xs mb-1">
        <span className="text-white/80">{count} / {cap}</span>
        {reached && (
          <span className="px-1.5 py-0.5 rounded bg-amber-500/20 text-amber-300 text-[10px] font-semibold">
            Cap Reached
          </span>
        )}
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className={`h-full ${reached ? 'bg-amber-400' : 'bg-emerald-400'}`}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

/**
 * Unified lead distribution: import to my account, or a weighted round-robin
 * pool with per-agent daily caps.
 * @param {Object} props
 * @param {{distributionMode: string, roundRobinConfig: {agents: Array<object>, overflowStrategy: string}}} props.form
 * @param {(path: string, value: *) => void} props.setField
 * @param {Array<{_id: string, name?: string, email?: string, avatar?: string}>} props.tenantAgents
 * @returns {JSX.Element}
 */
export default function DistributionSection({ form, setField, tenantAgents }) {
  const isRR = form.distributionMode === 'round_robin';
  const rr = form.roundRobinConfig || { agents: [], overflowStrategy: 'even_split' };
  const byId = new Map(tenantAgents.map((a) => [String(a._id), a]));
  const inPool = new Set(rr.agents.map((a) => String(a.agentId)));
  const available = tenantAgents.filter((a) => !inPool.has(String(a._id)));

  const setAgents = (agents) => setField('roundRobinConfig', { ...rr, agents });
  const patch = (agentId, body) =>
    setAgents(rr.agents.map((a) => (String(a.agentId) === String(agentId) ? { ...a, ...body } : a)));

  return (
    <div className="panel p-6" data-testid="distribution-section">
      <div className="flex items-center gap-2 mb-4">
        <Users className="w-4 h-4 text-civix-purpleSoft" />
        <h2 className="font-semibold">Lead distribution</h2>
      </div>

      <div className="space-y-3">
        {[
          { value: 'self', label: 'Import leads directly into my account (Default)' },
          { value: 'round_robin', label: 'Distribute to team via Weighted Round-Robin' },
        ].map((opt) => (
          <label key={opt.value} className="flex items-start gap-3 cursor-pointer">
            <input
              type="radio"
              name="distribution-mode"
              value={opt.value}
              checked={form.distributionMode === opt.value}
              onChange={() => setField('distributionMode', opt.value)}
              className="mt-0.5 accent-civix-purple"
            />
            <span className="text-sm text-white/80">{opt.label}</span>
          </label>
        ))}
      </div>

      {isRR && (
        <div className="mt-5 pt-4 border-t border-civix-border/40 space-y-4" data-testid="round-robin-pool">
          <select
            className="input w-full max-w-sm"
            data-testid="add-agent-to-pool"
            value=""
            disabled={available.length === 0}
            onChange={(e) => {
              if (!e.target.value) return;
              setAgents([...rr.agents, { agentId: e.target.value, enabled: true, weightCap: 10, assignedCountToday: 0 }]);
            }}
          >
            <option value="">{available.length ? '+ Add Agent to Pool' : 'All team members added'}</option>
            {available.map((a) => (
              <option key={a._id} value={a._id}>{a.name || a.email}</option>
            ))}
          </select>

          {rr.agents.length === 0 ? (
            <div className="text-xs text-white/50 py-3">
              No agents in the pool yet — leads will fall back to the manager until you add some.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[11px] uppercase tracking-wider text-white/40">
                    <th className="py-2 pr-3 font-medium">Active</th>
                    <th className="py-2 pr-3 font-medium">Agent</th>
                    <th className="py-2 pr-3 font-medium">Daily Lead Cap</th>
                    <th className="py-2 pr-3 font-medium">Received Today</th>
                    <th className="py-2 font-medium sr-only">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {rr.agents.map((row) => {
                    const agent = byId.get(String(row.agentId));
                    const cap = Math.max(1, parseInt(row.weightCap, 10) || 1);
                    return (
                      <tr key={row.agentId} className="border-t border-civix-border/30">
                        <td className="py-2 pr-3">
                          <input
                            type="checkbox"
                            checked={row.enabled !== false}
                            onChange={(e) => patch(row.agentId, { enabled: e.target.checked })}
                            className="accent-civix-purple"
                            aria-label="Active"
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <div className="flex items-center gap-2">
                            <Avatar agent={agent} />
                            <span className="truncate max-w-[160px]">{agent?.name || agent?.email || 'Unknown agent'}</span>
                          </div>
                        </td>
                        <td className="py-2 pr-3">
                          <input
                            type="number"
                            min={1}
                            className="input w-20"
                            value={row.weightCap}
                            onChange={(e) => patch(row.agentId, { weightCap: e.target.value })}
                            onBlur={() => patch(row.agentId, { weightCap: cap })}
                            aria-label="Daily lead cap"
                          />
                        </td>
                        <td className="py-2 pr-3">
                          <ReceivedToday count={row.assignedCountToday || 0} cap={cap} />
                        </td>
                        <td className="py-2 text-right">
                          <button
                            type="button"
                            onClick={() => setAgents(rr.agents.filter((a) => String(a.agentId) !== String(row.agentId)))}
                            className="p-1 text-white/40 hover:text-rose-400"
                            title="Remove from pool"
                            aria-label="Remove from pool"
                          >
                            <X className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          <div className="flex flex-wrap items-center gap-2 text-sm text-white/70">
            <span>When all agent caps are reached today:</span>
            <select
              className="input"
              data-testid="overflow-strategy"
              value={rr.overflowStrategy}
              onChange={(e) => setField('roundRobinConfig', { ...rr, overflowStrategy: e.target.value })}
            >
              {OVERFLOW_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
          </div>
        </div>
      )}
    </div>
  );
}
